import React, { useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import {
  FaHome, FaCode, FaProjectDiagram, FaGraduationCap,
  FaCertificate, FaEnvelope
} from 'react-icons/fa';
import { FiChevronRight } from 'react-icons/fi';
import DevAvatar from './DevAvatar';
import './Sidebar.css';

const ease = [0.22, 1, 0.36, 1];
const spring = { type: 'spring', stiffness: 520, damping: 40 };

const navItems = [
  { path: '/', icon: <FaHome />, label: 'Home' },
  { path: '/skills', icon: <FaCode />, label: 'Skills' },
  { path: '/projects', icon: <FaProjectDiagram />, label: 'Projects' },
  { path: '/timeline', icon: <FaGraduationCap />, label: 'Journey' },
  { path: '/certificates', icon: <FaCertificate />, label: 'Certificates' },
  { path: '/contact', icon: <FaEnvelope />, label: 'Contact' }
];

// Which page are we on? (the sidebar takes that page's colour)
const PAGES = ['skills', 'projects', 'timeline', 'certificates', 'contact'];
const ORDER = ['home', ...PAGES]; 
const getPage = (pathname) => {
  const seg = pathname.split('/')[1];
  return PAGES.includes(seg) ? seg : 'home';
};

const rise = (delay) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.5, ease }
});

const Sidebar = () => {
  const page = getPage(useLocation().pathname);

  const [fx, setFx] = useState({ page, dir: 'fwd' });
  if (fx.page !== page) {
    setFx({
      page,
      dir: ORDER.indexOf(page) >= ORDER.indexOf(fx.page) ? 'fwd' : 'back'
    });
  }
  const dir = fx.page === page ? fx.dir : 'fwd';

  /* ---- page-change effect: origin = active nav item ka center ---- */
  const asideRef = useRef(null);
  const mobileRef = useRef(null);

  useLayoutEffect(() => {
    const aside = asideRef.current;
    if (aside) {
      const item = aside.querySelector('.nav-item.active');
      if (item) {
        const a = aside.getBoundingClientRect();
        const r = item.getBoundingClientRect();
        aside.style.setProperty(
          '--fx-oy',
          `${r.top - a.top + r.height / 2 + aside.scrollTop}px`
        );
      }
    }
    const mob = mobileRef.current;
    if (mob) {
      const item = mob.querySelector('.mobile-nav-item.active');
      if (item) {
        const a = mob.getBoundingClientRect();
        const r = item.getBoundingClientRect();
        mob.style.setProperty('--fx-ox', `${r.left - a.left + r.width / 2}px`);
      }
    }
  }, [page]);

  // soft light that follows the mouse (mouse devices only, touch ignores it)
  const onMove = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  const onLeave = (e) => {
    e.currentTarget.style.removeProperty('--mx');
    e.currentTarget.style.removeProperty('--my');
  };

  return (
    <MotionConfig reducedMotion="user">
      {/* ================= Desktop / Tablet Sidebar ================= */}
      <motion.aside
        ref={asideRef}
        className="sidebar"
        data-sb-page={page}
        initial={{ opacity: 0, x: -24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.55, ease }}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
      >
        <span
          key={page}
          className={`sb-fx sb-fx-${page}`}
          data-dir={dir}
          aria-hidden="true"
        />
        {/* Profile */}
        <div className="sb-profile">
          <motion.div
            className="sb-avatar"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.6, ease }}
            whileHover={{ scale: 1.04 }}
          >
            <span className="sb-avatar-glow"></span>
            <img
              src="/images/amit-photo.jpg"
              alt="Amit Ingale"
              width="108"
              height="108"
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextSibling) {
                  e.currentTarget.nextSibling.style.display = 'flex';
                }
              }}
            />
            <div className="profile-placeholder"><span>AI</span></div>
            <span className="sb-avatar-ring"></span>
          </motion.div>

          <motion.h3 className="sb-name" {...rise(0.25)}>Amit Ingale</motion.h3>
          <motion.p className="sb-role" {...rise(0.32)}>Full Stack Developer</motion.p>
          <motion.span className="status" {...rise(0.39)}>
            <span className="status-dot"></span>
            Available for hire
          </motion.span>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav" aria-label="Main navigation">
          {navItems.map((n, index) => (
            <motion.div
              key={n.path}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35 + index * 0.06, duration: 0.45, ease }}
            >
              <NavLink
                to={n.path}
                end={n.path === '/'}
                title={n.label}
                aria-label={n.label}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-desktop"
                        className="nav-active-bg"
                        transition={spring}
                      >
                        <span className="nav-active-bar"></span>
                      </motion.span>
                    )}
                    <span className="nav-item-inner">
                      <motion.span
                        className="nav-icon"
                        animate={isActive ? { scale: [0.8, 1.15, 1] } : { scale: 1 }}
                        transition={{ duration: 0.45, ease }}
                      >
                        {n.icon}
                      </motion.span>
                      <span className="nav-label">{n.label}</span>
                      <FiChevronRight className="nav-arrow" />
                    </span>
                  </>
                )}
              </NavLink>
            </motion.div>
          ))}
        </nav>

        {/* Cartoon developer: reacts to clicks anywhere in the portfolio */}
        <div className="sb-dev">
          <DevAvatar page={page} />
        </div>
      </motion.aside>

      {/* ================= Mobile Bottom Navigation ================= */}
      <motion.nav
        ref={mobileRef}
        className="mobile-bottom-nav"
        data-sb-page={page}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease }}
        aria-label="Main navigation"
      >
        <span
          key={page}
          className={`sb-fx sb-fx-${page}`}
          data-dir={dir}
          aria-hidden="true"
        />
        {navItems.map((n) => (
          <NavLink
            key={n.path}
            to={n.path}
            end={n.path === '/'}
            className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="nav-active-mobile"
                    className="mobile-active-bg"
                    transition={spring}
                  />
                )}
                <span className="mobile-nav-content">
                  <span className="mobile-nav-icon">{n.icon}</span>
                  <span className="mobile-nav-label">{n.label}</span>
                </span>
              </>
            )}
          </NavLink>
        ))}
      </motion.nav>
    </MotionConfig>
  );
};

export default Sidebar;
